/**
 * The weather module's data layer. Everything goes through `api` (src/lib/api.ts) — no raw
 * fetch anywhere, so a 403 for a kid or a 502 upstream outage arrives as an ApiRequestError
 * the UI can read rather than as a crash.
 */
import { api, ApiRequestError } from '../../lib/api';
import type { Weather, HomeLocation } from '../../shared/types';

export type { HomeLocation };

export const getWeather = () => api.get<Weather>('/weather');
export const getLocation = () => api.get<HomeLocation | null>('/weather/location');
export const putLocation = (loc: HomeLocation) =>
  api.put<HomeLocation>('/weather/location', loc);

/**
 * The API's own words when it has them. Never invent a cause — an unexplained failure says
 * so plainly instead of guessing at a reason we haven't established.
 */
export function explain(err: unknown, fallback: string): string {
  if (err instanceof ApiRequestError) {
    if (err.body.detail) return err.body.detail;
    if (err.status === 403) return 'Only a grown-up can do that.';
    if (err.status >= 500) return 'The weather service is having trouble. Try again in a moment.';
  }
  return fallback;
}

// ---------------------------------------------------------------- date + temp formatting

/** '72' from 72 — °F, whole degrees, sign preserved. */
export function temp(f: number): string {
  return `${Math.round(f)}°`;
}

/** Local YYYY-MM-DD for "today", so day comparisons never drift by a timezone. */
export function todayIso(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * 'YYYY-MM-DD' → 'Mon'. Parsed as a LOCAL date, never UTC midnight, so the weekday can't
 * slip a day. Returns the raw string if it can't be parsed rather than inventing a day.
 */
export function weekdayShort(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short' });
}

/** Is this 'YYYY-MM-DD' today, in the viewer's own timezone? */
export function isToday(iso: string, now = new Date()): boolean {
  return iso === todayIso(now);
}
